import { normalizeRank, BASELINE_ROLE_MAP, SERVICE_CATEGORIES, SERVICE_NAME_MAP } from '../../../components/bids/constants/bidWizardConstants';

const toStep4Category = (name) => SERVICE_NAME_MAP[name] ?? name;


// ─── Rate-to-role fallback ────────────────────────────────────────────────────

const RATE_TO_ROLE_MAP = [
  { minRate: 250, role: 'Principal'     },
  { minRate: 160, role: 'Sr AM'         },
  { minRate: 100, role: 'Acct Mgr'      },
  { minRate: 60,  role: 'Asst Acct Mgr' },
  { minRate: 50,  role: 'Mktg Support'  },
];

const normalizeServiceName = (raw) => {
  const trimmed = (raw || '').trim();
  if (SERVICE_NAME_MAP[trimmed]) return SERVICE_NAME_MAP[trimmed];
  const match = SERVICE_CATEGORIES.find(
    (s) => s.toLowerCase() === trimmed.toLowerCase()
  );
  return match || 'General';
};

export const mapRateToRole = (employeeRate) => {
  for (const { minRate, role } of RATE_TO_ROLE_MAP) {
    if (employeeRate >= minRate) return role;
  }
  return 'Mktg Support';
};

// Rank values that mean "unassigned" — fall through to rate-based mapping
const NULL_RANK_VALUES = new Set(['none', 'null', 'undefined', '', 'n/a', 'unknown']);

/**
 * Derive canonical role name from a component record.
 * Prefers rank field (normalized); rejects null/'None'/empty and falls back to rate mapping.
 */
export const resolveRole = (component) => {
  const rank = component.rank;
  if (rank && !NULL_RANK_VALUES.has(String(rank).trim().toLowerCase())) {
    return normalizeRank(rank);
  }
  if (component.employee_role && !NULL_RANK_VALUES.has(String(component.employee_role).trim().toLowerCase())) {
    return normalizeRank(component.employee_role);
  }
  return mapRateToRole(component.employee_rate || 0);
};

/**
 * Resolve a role string key (canonical or camelCase) to a canonical role name.
 * Used inside buildServicesFromBaseline where keys are strings, not component objects.
 */
const resolveRoleKey = (key) => normalizeRank(BASELINE_ROLE_MAP[key] ?? key);

// ─── componentsToServices ─────────────────────────────────────────────────────

/**
 * Convert engagement components (from useBidEngagementComponents) into the
 * wizard services[] format expected by Step4BuildServices.
 *
 * Step4 services shape:
 *   [{ category: string, items: [{ role, hours, minHours, maxHours, autoRange, notes }] }]
 *
 * Hours:
 *   • isRetainer — avg hours per month (total ÷ number of months)
 *   • isProject  — total hours across all months
 *
 * Range (minHours / maxHours):
 *   • Retainer with multiple months → real low/high monthly totals per role
 *   • Single month or project       → ±5% of avg
 */
export const componentsToServices = (
  components,
  engagementName = 'Services',
  isRetainer     = false,
) => {
  if (!components?.length) return null;
 
  // Deduplicate: keep latest created_at per month+employee+product_service
  const seen = {};
  components.forEach((c) => {
    const key = `${c.month}-${c.employee_name}-${c.product_service || ''}`;
    if (!seen[key] || c.created_at > seen[key].created_at) seen[key] = c;
  });
  const deduped = Object.values(seen).filter((c) => (c.hours || 0) > 0);
  if (!deduped.length) return null;
  
  const allMonths  = [...new Set(deduped.map((c) => c.month))];
  const monthCount = allMonths.length || 1;
 
  // ── Group by product_service ──────────────────────────────────────
  // Structure: { serviceName: { role: { totalHours, monthlyTotals, notes, bestRate } } }
  const serviceRoleMap = {};
 
  for (const comp of deduped) {
    const serviceName = normalizeServiceName(comp.product_service);
    const role        = resolveRole(comp);
    const hours       = comp.hours || 0;
    const month       = comp.month || 'unknown';
 
    if (!serviceRoleMap[serviceName]) {
      serviceRoleMap[serviceName] = {};
    }
 
    const roleMap = serviceRoleMap[serviceName];
 
    if (!roleMap[role]) {
      roleMap[role] = {
        totalHours:    0,
        monthlyTotals: {},   // { month: hours } for real variance on retainers
        notes:         [],
        bestRate:      0,
      };
    }
 
    roleMap[role].totalHours += hours;
    roleMap[role].monthlyTotals[month] = (roleMap[role].monthlyTotals[month] || 0) + hours;
 
    if (comp.employee_name && !roleMap[role].notes.includes(comp.employee_name)) {
      roleMap[role].notes.push(comp.employee_name);
    }
    if ((comp.employee_rate || 0) > roleMap[role].bestRate) {
      roleMap[role].bestRate = comp.employee_rate;
    }
  }
 
  // ── Build services array ──────────────────────────────────────────
  const serviceNames = Object.keys(serviceRoleMap).sort((a, b) => {
    if (a === 'Other') return 1;   // "Other" always last
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
 
  const services = serviceNames.map((serviceName) => {
    const roleMap = serviceRoleMap[serviceName];
 
    const items = Object.entries(roleMap)
      .map(([role, data]) => {
        const avg = isRetainer
          ? parseFloat((data.totalHours / monthCount).toFixed(1))
          : Math.round(data.totalHours);
 
        if (!avg) return null;
 
        // Real monthly variance for retainer with >1 month; ±5% fallback otherwise
        const monthlyValues = Object.values(data.monthlyTotals);
        let minHours, maxHours;
 
        if (isRetainer && monthlyValues.length > 1) {
          minHours = parseFloat(Math.min(...monthlyValues).toFixed(1));
          maxHours = parseFloat(Math.max(...monthlyValues).toFixed(1));
        } else {
          minHours = parseFloat((avg * 0.95).toFixed(1));
          maxHours = parseFloat((avg * 1.05).toFixed(1));
        }
 
        const nameList = data.notes.slice(0, 3).join(', ');
        const overflow = data.notes.length > 3 ? ` +${data.notes.length - 3} more` : '';
 
        return {
          role,
          hours:     avg,
          minHours,
          maxHours,
          autoRange: true,
          rate:      data.bestRate,
          notes:     `Actual: ${nameList}${overflow}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.hours - a.hours);
 
    if (!items.length) return null;
 
    return { category: serviceName, items };
  }).filter(Boolean);
 
  return services.length ? services : null;
};
 

// ─── Legacy helpers ───────────────────────────────────────────────────────────

/**
 * Legacy: Convert QB actuals data into wizard services format.
 */
export const actualsToServices = (actuals, engagementName = 'Services') => {
  if (!actuals?.employee_breakdown?.length) return null;

  const roleMap   = {};
  const roleNotes = {};

  for (const emp of actuals.employee_breakdown) {
    const role = mapRateToRole(emp.employee_rate || 0);
    if (!roleMap[role]) {
      roleMap[role]   = 0;
      roleNotes[role] = [];
    }
    roleMap[role] += emp.total_hours;
    roleNotes[role].push(`${emp.employee} (${emp.total_hours.toFixed(1)}h)`);
  }

  const items = Object.entries(roleMap)
    .map(([role, hours]) => ({
      role,
      hours: Math.round(hours),
      notes: `Actual: ${roleNotes[role].join(', ')}`,
    }))
    .sort((a, b) => b.hours - a.hours)
    .filter((item) => item.hours > 0);

  if (!items.length) return null;

  return [{ category: engagementName, items }];
};

export const getBaselineServices = (componentType, componentName, BASELINE_TEMPLATES) => {
  if (BASELINE_TEMPLATES[componentType]) {
    return BASELINE_TEMPLATES[componentType];
  }
  return [
    {
      category: componentName || 'Project Services',
      items: [
        { role: 'Acct Mgr',      hours: 40, minHours: 38, maxHours: 42, autoRange: true, notes: 'Project management and execution' },
        { role: 'Asst Acct Mgr', hours: 20, minHours: 19, maxHours: 21, autoRange: true, notes: 'Support and coordination' },
      ],
    },
  ];
};

export const engagementToServices = (engagement) => {
  if (engagement.services && Array.isArray(engagement.services) && engagement.services.length > 0) {
    return engagement.services.map((s) => ({
      category: s.category || s.name,
      items: (s.items || []).map((item) => ({
        role:      normalizeRank(item.role || 'Acct Mgr'),
        hours:     item.hours    || 0,
        minHours:  item.minHours ?? parseFloat(((item.hours || 0) * 0.95).toFixed(1)),
        maxHours:  item.maxHours ?? parseFloat(((item.hours || 0) * 1.05).toFixed(1)),
        autoRange: item.autoRange ?? true,
        notes:     item.notes    || '',
      })),
    }));
  }

  const totalHours     = engagement.estimated_hours || 40;
  const primaryName    = engagement.scope_summary || engagement.engagement_name || 'Services';
  const estimatedValue = engagement.estimated_value || 0;
  const isLarge        = estimatedValue > 100000 || totalHours > 200;

  const rawItems = isLarge
    ? [
        { role: 'Principal',     hours: Math.round(totalHours * 0.08), notes: 'Strategic oversight'   },
        { role: 'Sr AM',         hours: Math.round(totalHours * 0.20), notes: 'Account leadership'    },
        { role: 'Acct Mgr',      hours: Math.round(totalHours * 0.40), notes: 'Day-to-day management' },
        { role: 'Asst Acct Mgr', hours: totalHours - Math.round(totalHours * 0.68), notes: 'Execution support' },
      ]
    : [
        { role: 'Acct Mgr',      hours: Math.round(totalHours * 0.6), notes: 'Project management and execution' },
        { role: 'Asst Acct Mgr', hours: Math.round(totalHours * 0.4), notes: 'Support and coordination'        },
      ];

  const items = rawItems
    .filter((i) => i.hours > 0)
    .map((i) => ({
      ...i,
      minHours:  parseFloat((i.hours * 0.95).toFixed(1)),
      maxHours:  parseFloat((i.hours * 1.05).toFixed(1)),
      autoRange: true,
    }));

  const services = [{ category: primaryName, items }];

  if (engagement.lessons_learned && services[0]?.items?.[0]) {
    services[0].items[0].notes += ` | Lessons: ${engagement.lessons_learned}`;
  }

  return services;
};

// ─── Utilities ────────────────────────────────────────────────────────────────

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const formatCurrency = (value) =>
  value
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
    : '$0';



// ─── Baseline → Step4 services converter ─────────────────────────────────────

/**
 * Build items[] from explicit roleHrs (preset baselines).
 * Keys are canonical role names e.g. { 'Sr AM': 30, 'Asst Acct Mgr': 42 }
 */
function itemsFromRoleHrs(roleHrs, label) {
  return Object.entries(roleHrs)
    .filter(([, hrs]) => (parseFloat(hrs) || 0) > 0)
    .map(([key, hrs]) => {
      const h = parseFloat(hrs);
      return {
        role:      resolveRoleKey(key),   // string key, not a component object
        hours:     h,
        minHours:  parseFloat((h * 0.95).toFixed(1)),
        maxHours:  parseFloat((h * 1.05).toFixed(1)),
        autoRange: true,
        notes:     `From ${label} baseline`,
      };
    });
}

/**
 * Build items[] by distributing annualHrs across a percentage mix object.
 * Keys may be camelCase (srAM, acctMgr) or canonical ('Sr AM').
 */
function itemsFromMix(annualHrs, mix, label) {
  const entries = Object.entries(mix).filter(([, pct]) => (parseFloat(pct) || 0) > 0);
  if (!entries.length || !annualHrs) return [];

  const totalPct = entries.reduce((s, [, p]) => s + (parseFloat(p) || 0), 0);
  if (!totalPct) return [];

  return entries
    .map(([key, pct]) => {
      const share = (parseFloat(pct) || 0) / totalPct;
      const h     = parseFloat((annualHrs * share).toFixed(1));
      if (!h) return null;
      return {
        role:      resolveRoleKey(key),   // handles both camelCase and canonical
        hours:     h,
        minHours:  parseFloat((h * 0.95).toFixed(1)),
        maxHours:  parseFloat((h * 1.05).toFixed(1)),
        autoRange: true,
        notes:     `From ${label} baseline`,
      };
    })
    .filter(Boolean);
}

/**
 * Convert a Step3 baseline object into the services[] array that
 * Step4BuildServices / wizardData.services expects.
 *
 * Resolution priority per service:
 *   1. service.roleHrs      — preset baselines (explicit hrs per role)
 *   2. service.staffMix     — custom baseline per-service override
 *   3. baseline.globalMix   — custom baseline global fallback
 *   4. baseline.staffMix    — legacy preset shape (safety net)
 */
export function buildServicesFromBaseline(baseline) {
  if (!baseline?.services) return [];

  const label     = baseline.label   || 'baseline';
  const globalMix = baseline.globalMix || null;
  const legacyMix = baseline.staffMix  || null;

  const step4Services = [];

  for (const [serviceName, data] of Object.entries(baseline.services)) {
    const annualHrs = parseFloat(data?.annualHrs) || 0;
    if (!annualHrs) continue;

    let items = [];

    if (data.roleHrs && Object.keys(data.roleHrs).length > 0) {
      items = itemsFromRoleHrs(data.roleHrs, label);
    } else if (data.staffMix && Object.keys(data.staffMix).length > 0) {
      items = itemsFromMix(annualHrs, data.staffMix, label);
    } else if (globalMix && Object.keys(globalMix).length > 0) {
      items = itemsFromMix(annualHrs, globalMix, label);
    } else if (legacyMix && Object.keys(legacyMix).length > 0) {
      items = itemsFromMix(annualHrs, legacyMix, label);
    }

    if (!items.length) continue;

    step4Services.push({
      category: toStep4Category(serviceName),
      items,
    });
  }

  return step4Services;
}